import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Save, Baby } from 'lucide-react';

export const BirthFormDialog = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  onSubmit,
  femelles,
  males,
  breeds,
  sheep,
  creerAgneaux,
  setCreerAgneaux,
  agneauxData,
  setAgneauxData,
  updateAgneau,
  initializeAgneaux,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Declarer une naissance</DialogTitle>
        <DialogDescription>Ajoutez une nouvelle declaration de naissance et creez les agneaux</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="mere_id">Mere *</Label>
            <Select
              value={formData.mere_id}
              onValueChange={(value) => {
                setFormData({ ...formData, mere_id: value });
                if (agneauxData.length > 0) {
                  const mere = sheep.find(s => s.id === value);
                  if (mere) {
                    setAgneauxData(agneauxData.map(a => ({ ...a, race: mere.race })));
                  }
                }
              }}
            >
              <SelectTrigger data-testid="birth-mere-select"><SelectValue placeholder="Selectionner la mere" /></SelectTrigger>
              <SelectContent>
                {femelles.map((s) => (<SelectItem key={s.id} value={s.id}>N {s.numero} {s.nom && `- ${s.nom}`}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="pere_id">Pere (optionnel)</Label>
            <Select value={formData.pere_id || "none"} onValueChange={(value) => setFormData({ ...formData, pere_id: value === "none" ? "" : value })}>
              <SelectTrigger data-testid="birth-pere-select"><SelectValue placeholder="Selectionner le pere" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun / Inconnu</SelectItem>
                {males.map((s) => (<SelectItem key={s.id} value={s.id}>N {s.numero} {s.nom && `- ${s.nom}`}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="date_prevue">Date de naissance *</Label>
            <Input id="date_prevue" type="date" value={formData.date_prevue} onChange={(e) => setFormData({ ...formData, date_prevue: e.target.value })} required data-testid="birth-date-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="nombre_agneaux">Nombre d'agneaux a creer</Label>
            <Input
              id="nombre_agneaux" type="number" min="1" max="5" value={formData.nombre_agneaux}
              onChange={(e) => {
                const count = parseInt(e.target.value) || 1;
                setFormData({ ...formData, nombre_agneaux: count });
                if (creerAgneaux) initializeAgneaux(count);
              }}
              data-testid="birth-nombre-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="notes">Notes</Label>
          <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} placeholder="Notes sur cette naissance..." data-testid="birth-notes-input" />
        </div>

        <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-900 dark:bg-slate-900">
          <div className="flex items-center gap-3">
            <input
              type="checkbox" id="creer_agneaux" checked={creerAgneaux}
              onChange={(e) => {
                setCreerAgneaux(e.target.checked);
                if (e.target.checked) { initializeAgneaux(formData.nombre_agneaux || 1); }
                else { setAgneauxData([]); }
              }}
              className="w-5 h-5 text-slate-600 dark:text-slate-400 rounded"
            />
            <Label htmlFor="creer_agneaux" className="text-base font-medium cursor-pointer">
              Creer les agneaux maintenant
            </Label>
          </div>
          <p className="text-sm text-gray-500 mt-1 ml-8">Les agneaux seront ajoutes au troupeau avec un lien vers leur mere</p>
        </div>

        {creerAgneaux && agneauxData.length > 0 && (
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <Baby className="w-5 h-5" />
              Informations des agneaux ({agneauxData.length})
            </h3>
            {agneauxData.map((agneau, index) => (
              <div key={agneau.numero || `agneau-${index}`} className="border rounded-lg p-4 bg-white dark:bg-gray-800 space-y-3">
                <div className="font-medium text-slate-600 dark:text-slate-400">Agneau #{index + 1}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Numero *</Label>
                    <Input value={agneau.numero} onChange={(e) => updateAgneau(index, 'numero', e.target.value)} placeholder="001" required />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nom</Label>
                    <Input value={agneau.nom} onChange={(e) => updateAgneau(index, 'nom', e.target.value)} placeholder="Nom (optionnel)" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Sexe *</Label>
                    <Select value={agneau.sexe} onValueChange={(value) => updateAgneau(index, 'sexe', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="femelle">Femelle</SelectItem>
                        <SelectItem value="male">Male</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Race</Label>
                    <Select value={agneau.race} onValueChange={(value) => updateAgneau(index, 'race', value)}>
                      <SelectTrigger><SelectValue placeholder="Race" /></SelectTrigger>
                      <SelectContent>
                        {breeds.map((b) => (<SelectItem key={b.id} value={b.nom}>{b.nom}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Poids (kg)</Label>
                    <Input type="number" step="0.1" value={agneau.poids} onChange={(e) => updateAgneau(index, 'poids', e.target.value)} placeholder="0.0" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-3 pt-4">
          <Button type="submit" className="flex-1 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600" data-testid="birth-submit-button">
            <Save className="w-4 h-4 mr-2" />
            {creerAgneaux ? `Declarer et creer ${agneauxData.length} agneau(x)` : 'Declarer la naissance'}
          </Button>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);
